// Shared client-side re-encode: whatever image format is picked, convert to
// WebP (and downscale if oversized) before it ever reaches storage, so the
// bucket never accumulates large PNG/JPEG originals.
//
// With `trimTransparent`, fully transparent margins are cropped away first
// (plus a small even padding), so a logo exported on a large empty canvas
// fills the space it is given instead of rendering tiny.
export async function convertToWebp(file: File, maxDimension: number, quality = 0.85, trimTransparent = false): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  let { width, height } = bitmap;
  if (width > maxDimension || height > maxDimension) {
    const scale = maxDimension / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Image processing is not supported in this browser.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const out = trimTransparent ? trimTransparentMargins(canvas) : canvas;
  return await new Promise<Blob>((resolve, reject) => {
    out.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Failed to convert image to WebP.'))), 'image/webp', quality);
  });
}

function trimTransparentMargins(src: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = src.getContext('2d', { willReadFrequently: true });
  if (!ctx) return src;
  const { width, height } = src;
  const data = ctx.getImageData(0, 0, width, height).data;
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  // Fully transparent image, or nothing to trim: keep the original.
  if (maxX < 0 || (minX === 0 && minY === 0 && maxX === width - 1 && maxY === height - 1)) return src;
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const pad = Math.round(Math.max(w, h) * 0.03);
  const out = document.createElement('canvas');
  out.width = w + pad * 2;
  out.height = h + pad * 2;
  out.getContext('2d')?.drawImage(src, minX, minY, w, h, pad, pad, w, h);
  return out;
}
